import React, { useState, useEffect, useCallback } from "react";
import { useTheme } from "../../../context/ThemeContext";

import { useBreakpoint } from "../../../hooks/useBreakpoint";

import {
  firestore,
  collection,
  getDocs,
  query,
  where,
  limit,
  startAfter,
  orderBy,
  getDoc,
  doc
} from "../../../firebase";

import CtaButton from "../../shared/buttons/ctaButton";
import GviFlexTestimonialRating from "../../../components/cards/gviFlexTestimonialRating"; // Added this line
import CollapsibleWrapper from "../../shared/collapsibleWrapper";

import ProfileImgPlaceholder from "../../../images/icons/reviews-placeholder-img.inline.svg";
import Image from "../../image";

const TestimonialIndividualImage = ({ imageSrc }) => {
  return (
    <Image
      sourceUrl={imageSrc}
      imageClass="w-16 h-16 fill-tertiary-bg planet:fill-tertiary-text rounded-full object-cover object-center"
    />
  );
};

const ProgramTestimonials = ({ TestimonialProgramCode = "MXPM0066E" }) => {
  const { currentBrand } = useTheme();

  const breakpoint = useBreakpoint();

  const [testimonialsLoading, setTestimonialsloading] = useState(true);
  const [testimonialsList, setTestimonialsList] = useState([]);
  const [showPagination, setPagination] = useState(false);
  const [reviewsCount, setReviewsCount] = useState(0);
  const [reviewsVisibleCount, setReviewsVisibleCount] = useState(0);

  const resultsLimit = breakpoint === "xs" ? 1 : 3;

  const siteBrand = currentBrand === "planet" ? "Planet" : "People";

  async function getTestimonials() {
    let testimonialsFilter = "";
    testimonialsFilter = query(
      collection(firestore, "marketing-stories"),
      where("TestimonialSiteBrand", "==", siteBrand),
      where("TestimonialProgramCode", "==", TestimonialProgramCode),
      where("TestimonialShowOnWebsite", "==", true),
      orderBy("TestimonialDatePosted", "desc"),
      limit(resultsLimit)
    );

    const testimonialsSnapshot = await getDocs(testimonialsFilter);
    return testimonialsSnapshot.docs.map((doc) => ({
      docId: doc.id,
      ...doc.data()
    }));
  }

  useEffect(() => {
    if (reviewsCount > 0) setPagination(reviewsCount > reviewsVisibleCount);
  }, [reviewsCount, reviewsVisibleCount]);

  useEffect(() => {
    getTestimonials().then((testimonials) => {
      setReviewsVisibleCount(testimonials.length);
      setReviewsCount(testimonials.length);
      setTestimonialsList({
        results: testimonials,
        lastKey: testimonials[testimonials.length - 1]?.docId,
        programCode: TestimonialProgramCode
      });

      setTestimonialsloading(false);
    });
  }, []);

  const handlePagination = useCallback(
    async (TestimonialProgramCode, lastKey) => {
      const getTestimonialsNextPage = async (listItem, docId) => {
        if (!docId) {
          return {};
        }
        const testimonialRef = collection(firestore, "marketing-stories");
        const docSnap = await getDoc(doc(testimonialRef, docId));

        const nextTestimonialPage = query(
          testimonialRef,
          where("TestimonialSiteBrand", "==", siteBrand),
          where("TestimonialProgramCode", "==", TestimonialProgramCode),
          where("TestimonialShowOnWebsite", "==", true),
          orderBy("TestimonialDatePosted", "desc"),
          limit(resultsLimit),
          startAfter(docSnap)
        );

        const nextTestimonialsSnapshot = await getDocs(nextTestimonialPage);

        return nextTestimonialsSnapshot.docs.map((doc) => ({
          docId: doc.id,
          ...doc.data()
        }));
      };

      await getTestimonialsNextPage(TestimonialProgramCode, lastKey).then(
        (nextTestimonialsList) => {
          if (nextTestimonialsList.length > 0) {
            setTestimonialsList({
              results: [...testimonialsList.results, ...nextTestimonialsList],
              lastKey:
                nextTestimonialsList[nextTestimonialsList.length - 1]?.docId,
              programCode: TestimonialProgramCode
            });
          }

          setReviewsVisibleCount(
            reviewsVisibleCount + nextTestimonialsList.length
          );

          setTestimonialsloading(false);
        }
      );
    },
    [resultsLimit, reviewsVisibleCount, siteBrand, testimonialsList]
  );

  if (!testimonialsLoading && testimonialsList?.results?.length < 1) {
    return <></>;
  }

  return (
    <>
      <section
        id="flex-testimonial-section"
        className="relative overflow-x-hidden w-full bg-primary-bg 2xl:container 2xl:m-auto"
      >
        {!testimonialsLoading && (
          <div className="w-11/12 mr-auto border-t border-gray-generic planet:border-gray-standard planet:sm:border-gray-light mx-auto pt-12 mt-12 md:mt-0 md:pb-20 sm:py-12 sm:w-10/12 2xl:w-full">
            <div className="relative w-full md:pt-8 md:pb-20">
              {reviewsCount && (
                <GviFlexTestimonialRating
                  containerClass="reviews-details flex items-center text-xl md:text-[32px]"
                  programCode={TestimonialProgramCode}
                  currentBrand={currentBrand}
                  iconClass="w-8 h-8 fill-gray-dark planet:fill-white"
                  expandedScoreContainerClass="text-black-lighter planet:text-white font-light md:font-bold pl-5"
                  expandedCountContainerClass="text-black-lighter planet:text-gray-generic font-light pl-1"
                  layout="flex-expanded"
                  reviewsCount={reviewsCount}
                  setReviewsCount={setReviewsCount}
                />
              )}

              {testimonialsList?.results?.map((testimonialDetail, slideIDX) => {
                const testimonialDate = new Date(
                  testimonialDetail?.TestimonialDatePosted
                ).toLocaleDateString("en-US", {
                  month: "long",
                  year: "numeric"
                });

                const testimonialTitle =
                  testimonialDetail?.TestimonialParticipantName;

                const testimonialDescription =
                  testimonialDetail?.TestimonialSummary?.replace(
                    /<span([^>]+)>|<b([^>]+)>|<h([^>]+)>/gim,
                    ""
                  );

                return (
                  <React.Fragment
                    key={`tab-wrapper-${slideIDX}-${testimonialDetail?.TestimonialId}`}
                  >
                    <div
                      key={`testimonial-details-${testimonialDetail?.TestimonialId}`}
                      className={
                        "flex flex-col my-5 md:my-10 md:mb-0 first-of-type:mt-0 planet:md:my-6 tab-content_html"
                      }
                    >
                      <div className="text-generic-text planet:text-gray-generic">
                        <div className="flex justify-between py-5 ">
                          <div className="flex justify-center">
                            <div className="testimonial-img">
                              {testimonialDetail.TestimonialFeaturedImageSrc ? (
                                <TestimonialIndividualImage
                                  imageSrc={
                                    testimonialDetail.TestimonialFeaturedImageSrc
                                  }
                                />
                              ) : (
                                <ProfileImgPlaceholder className="w-16 h-16" />
                              )}
                            </div>
                            <div className="user-details pl-4 md:pl-7 flex flex-col justify-center">
                              <div
                                className="text-generic-text planet:text-white font-bold"
                                dangerouslySetInnerHTML={{
                                  __html: testimonialTitle
                                }}
                              />
                              <div
                                className="text-generic-text planet:text-gray-generic"
                                dangerouslySetInnerHTML={{
                                  __html: `${testimonialDate} `
                                }}
                              />
                            </div>
                          </div>
                        </div>
                        <div className="text-generic-text planet:text-gray-generic">
                          <CollapsibleWrapper
                            desc={testimonialDescription}
                            summary={
                              breakpoint === "xs"
                                ? [
                                    testimonialDescription?.slice(0, 320),
                                    "..."
                                  ].join("")
                                : testimonialDescription
                            }
                            visibleLength={320}
                            visibleLines={2}
                            descStyle={`collapsible-wrapper`}
                            btnStyle="text-generic-text underline font-lato font-bold planet:rounded-sm planet:text-white"
                            wrapperContainer="text-section-desc-text flex-1 text-generic-text mb-4 tab-content_html testimonial-tab-item overflow-auto planet:text-gray-generic"
                            type={"both"}
                          />
                        </div>
                      </div>
                    </div>
                  </React.Fragment>
                );
              })}

              {showPagination && (
                <div className={"m-auto w-full pb-10 md:pb-0"}>
                  <CtaButton
                    ctaLabel="View more"
                    preset={"tertiary"}
                    onClick={() =>
                      handlePagination(
                        testimonialsList?.programCode,
                        testimonialsList?.lastKey
                      )
                    }
                    showArrowIcon={false}
                    labelClass="text-base leading-1 pr-2 "
                    extraButtonStyle={
                      "w-full sm:w-60 m-auto planet:hover:text-gray-dark planet:bg-gray-dark planet:xxs:border-white planet:sm:border-white"
                    }
                    showAsButton={true}
                  />
                </div>
              )}
            </div>
          </div>
        )}
      </section>
    </>
  );
};

export default ProgramTestimonials;
